<style lang="scss">
.pac-container {
    box-shadow: none;
    @apply translate-y-[-3px] bg-gray-700 pt-1 rounded-none border-t-0 after:-translate-x-1 after:-translate-y-1;

    &:after {
        content: 'Powered by Google';
        font-size: 10px;
        background: none !important;
        @apply text-gray-400;
    }

    .pac-item {
        @apply p-2 py-xxs text-gray-500 text-xs border-t-gray-800 z-10;

        .pac-icon {
            @apply mx-[.9375rem];
        }

        .pac-item-query {
            @apply text-sm text-gray-500 mr-1;

            .pac-matched {
                @apply text-gray-400;
            }
        }

        &:hover {
            @apply bg-gray-600 text-gray-400;

            .pac-item-query {
                @apply text-sm text-gray-400 mr-1;

                .pac-matched {
                    @apply text-gray-300;
                }
            }
        }
    }
}
</style>

<template>
    <div class="grid gap-sm sm:gap-md grid-cols-12" ref="supportcontainer">
        <div class="hidden lg:block lg:col-span-1"></div>
        <div class="col-span-12 lg:col-span-10">
            <div class="w-full md:w-5/12 relative mb-md">
                <label class="font-semibold mb-xxs inline-block" for="location">Locatie</label>

                <div class="relative">
                    <vue-google-autocomplete
                        class="form__input form__input--filled-dark"
                        ref="locationAutocomplete"
                        id="location"
                        name="searchQuery"
                        :types="''"
                        placeholder="Vul hier je postcode in"
                        :country="['be']"
                        v-on:placechanged="getAddressData"
                    ></vue-google-autocomplete>

                    <div class="absolute right-sm top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none">
                        <i class="fa-regular fa-search"></i>
                    </div>
                </div>
            </div>

            <div class="flex flex-wrap gap-sm items-center justify-start mb-lg">
                <template v-for="(item, index) in categories">
                    <a
                        v-if="item.count > 0"
                        href="#"
                        :key="index"
                        @click.prevent="filter(item.id)"
                        class="py-xxs px-xs text-xs uppercase hover:text-primary-400 hover:border-primary-400 text-gray-500 border transition-colors inline-flex border-gray-600 text-opacity-80 tracking-[2px] font-semibold cursor-pointer select-none"
                        :class="{
                            'bg-primary-400 !text-gray-800': active_categories.includes(item.id),
                        }"
                    >
                        {{ item.name }}
                    </a>
                </template>
            </div>

            <div key="loading" class="flex justify-center" v-if="loading">
                <div class="aspect-[320/520] w-1/4 relative mb-sm sm:mb-md flex justify-center items-center">
                    <i class="fa-regular fa-spinner-third h-[60px] w-auto animate-spin"></i>
                </div>
            </div>
            <div v-else class="grid grid-cols-12 gap-x-md md:gap-x-0 gap-y-lg md:gap-y-xl">
                <div class="col-span-6 md:col-span-4 md:hidden" v-for="article in sortedArticles" :key="article.id">
                    <supportitem :content="article" :distance-object="getDistanceForId(article.id)" />
                </div>
                <div class="col-span-12 hidden md:grid grid-cols-12 gap-y-lg md:gap-y-xl gap-x-md" :class="chunkindex % 2 === 0 ? 'sm:pr-lg' : 'sm:pl-lg'" v-for="(chunk, chunkindex) in articleChunks" :key="`chunk-${chunkindex}`">
                    <div v-for="item in chunk" :key="item.id" class="col-span-6 md:col-span-4">
                        <supportitem :content="item" :distance-object="getDistanceForId(item.id)" />
                    </div>
                </div>

                <div class="col-span-12" v-if="!distances">
                    <pagination :current-page="params.page" :total-pages="totalPages" @next-page="filter(null, params.page + 1)" @previous-page="filter(null, params.page - 1)" @go-to-page="(event) => filter(null, event.page)"> </pagination>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import supportitem from './item';
var scrollIntoView = require('scroll-into-view');

export default {
    name: 'supportoverview',

    data() {
        return {
            params: {
                per_page: window.innerWidth >= 1024 ? 9 : 10,
                page: 1,
                paginate: true,
                'support-categories': [],
            },
            loading: false,
            articles: [],
            categories: [],
            active_categories: [],
            totalPages: 0,
            searchQuery: '',
            lat: null,
            lng: null,
            distances: null,
            distanceLookup: {},
        };
    },

    mounted() {
        this.getArticleCategories();
        this.getArticles(false);
    },

    methods: {
        filter(id, page = null) {
            if (id) {
                if (this.active_categories.includes(id)) {
                    this.active_categories = this.active_categories.filter((el) => el !== id);
                } else {
                    this.active_categories.push(id);
                }

                this.params['support-categories'] = this.active_categories;
            }

            if (page) {
                this.params.page = page;
            } else {
                this.params.page = 1;
            }

            this.getArticles();
        },

        getArticles(scroll = true) {
            this.loading = true;
            let params = JSON.parse(JSON.stringify(this.params));

            if (scroll) {
                scrollIntoView(this.$refs.supportcontainer);
            }

            if (this.location) {
                params.per_page = 99;
            }

            axios
                .get('/api/support-articles', { params: { ...params } })
                .then((response) => {
                    if (response.data.status === 200) {
                        this.articles = response.data.data.results;

                        if (this.location) {
                            this.calculateAllDistances();
                        }

                        this.totalPages = Math.ceil(parseInt(response.data.data.meta.total) / this.params.per_page);
                    } else {
                        this.articles = [];
                    }
                })
                .finally(() => (this.loading = false));
        },

        getArticleCategories() {
            let params = JSON.parse(JSON.stringify(this.params));
            params.per_page = 99;

            axios.get('/api/support-categories', { params: { ...params } }).then((response) => {
                if (response.data.status === 200) {
                    this.categories = response.data.data.results;
                } else {
                    this.categories = [];
                }
            });
        },

        getAddressData: function (addressData, placeResultData, id) {
            this.location = placeResultData;
            this.lat = placeResultData.geometry.location.lat();
            this.lng = placeResultData.geometry.location.lng();

            this.getArticles();
        },

        calculateAllDistances() {
            const self = this;
            let distances = [];
            let distanceLookup = {};

            self.articles.forEach((o) => {
                let shortestLocation = o.acf.address_google_maps;
                let shortestDistance = self.calculateDistance(self.lat, self.lng, o.acf.address_google_maps.lat, o.acf.address_google_maps.lng, 'K');
                if (o.acf.other_addresses) {
                    o.acf.other_addresses.forEach((other) => {
                        let otherDistance = self.calculateDistance(self.lat, self.lng, other.address_google_maps.lat, other.address_google_maps.lng, 'K');
                        if (otherDistance < shortestDistance) {
                            shortestDistance = otherDistance;
                            shortestLocation = other.address_google_maps;
                        }
                    });
                }

                let distanceObject = {
                    id: o.id,
                    location: shortestLocation,
                    distance: Math.round(shortestDistance),
                };
                distances.push(distanceObject);
                distanceLookup[o.id] = distanceObject.distance;
            });

            function compare(a, b) {
                if (a.distance < b.distance) {
                    return -1;
                }
                if (a.distance > b.distance) {
                    return 1;
                }
                return 0;
            }

            distances.sort(compare);
            self.distances = distances;
            self.distanceLookup = distanceLookup;
        },

        calculateDistance(lat1, lon1, lat2, lon2, unit) {
            if (lat1 == lat2 && lon1 == lon2) {
                return 0;
            } else {
                var radlat1 = (Math.PI * lat1) / 180;
                var radlat2 = (Math.PI * lat2) / 180;
                var theta = lon1 - lon2;
                var radtheta = (Math.PI * theta) / 180;
                var dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
                if (dist > 1) {
                    dist = 1;
                }
                dist = Math.acos(dist);
                dist = (dist * 180) / Math.PI;
                dist = dist * 60 * 1.1515;
                if (unit == 'K') {
                    dist = dist * 1.609344;
                }
                if (unit == 'N') {
                    dist = dist * 0.8684;
                }
                return dist;
            }
        },

        getDistanceForId(id) {
            if (this.distances && this.distances.length) {
                const distanceobjects = this.distances.filter((d) => {
                    return id === d.id;
                });

                if (distanceobjects.length) {
                    return distanceobjects[0];
                }
            }
            return false;
        },
    },

    computed: {
        articleChunks() {
            const perChunk = 3; // items per chunk

            const result = this.sortedArticles.reduce((resultArray, item, index) => {
                const chunkIndex = Math.floor(index / perChunk);

                if (!resultArray[chunkIndex]) {
                    resultArray[chunkIndex] = []; // start a new chunk
                }

                resultArray[chunkIndex].push(item);

                return resultArray;
            }, []);

            return result;
        },

        sortedArticles() {
            if (this.distances != null && this.distances.length === this.articles.length) {
                let sortedArticles = [];

                this.distances.forEach((d) => {
                    let article = null;
                    let filteredArticles = this.articles.filter((o) => {
                        return o.id === d.id;
                    });

                    if (filteredArticles.length > 0) {
                        article = filteredArticles[0];
                    }

                    sortedArticles.push(article);
                });

                return sortedArticles;
            }
            return this.articles;
        },
    },

    components: {
        supportitem,
    },
};
</script>
